import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  Stack,
  Divider,
  Alert,
} from "@mui/material";
import { useLoginCheck } from "../../hooks/useLoginCheck";
import { lineConsole } from "../../utils/lineConsoleClient";
import "../../styles/scss/segmentBroadcast.scss";

// MUI Select のドロップダウンは portal に描画されるので、専用クラスでダークテーマを当てる。
const MENU_PROPS = { classes: { paper: "" }, className: "segment-broadcast-menu" };

const SEGMENT_TYPES = [
  { value: "all", label: "友だち全員" },
  { value: "status_tag", label: "LINEステータス別" },
  { value: "member_status", label: "会員ステータス別" },
  { value: "studio", label: "店舗別" },
  { value: "lesson", label: "レッスン別" },
  { value: "members", label: "個別に会員を選ぶ" },
];

const MEMBER_STATUS_LABEL = {
  active: "在籍",
  dormant: "2週以上欠席",
  payment_alert: "支払い異常",
  withdrawn: "退会",
};

const CTA_LABEL_MAX = 20;

function SegmentBroadcastPage() {
  useLoginCheck();

  const [options, setOptions] = useState(null);
  const [broadcasts, setBroadcasts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  // フォーム
  const [name, setName] = useState("");
  const [body, setBody] = useState("");
  const [ctaLabel, setCtaLabel] = useState("");
  const [ctaUrl, setCtaUrl] = useState("");
  const [fireAt, setFireAt] = useState("");
  const [segType, setSegType] = useState("all");
  const [segValue, setSegValue] = useState("");
  // 個別に選んだ会員 [{member_ref, member_name, display_name}]
  const [pickedMembers, setPickedMembers] = useState([]);

  // 開いている配信（保存後に id が入る）
  const [currentId, setCurrentId] = useState(null);
  const [currentStatus, setCurrentStatus] = useState("draft");
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [testMember, setTestMember] = useState(null);

  const readOnly = currentStatus === "sent";

  const loadOptions = useCallback(async () => {
    try {
      setOptions(await lineConsole.segmentOptions());
    } catch (e) {
      setError(`選択肢の取得に失敗しました: ${e.message}`);
    }
  }, []);

  const loadBroadcasts = useCallback(async () => {
    try {
      const data = await lineConsole.listBroadcasts();
      setBroadcasts(data.broadcasts || []);
    } catch (e) {
      setError(`配信一覧の取得に失敗しました: ${e.message}`);
    }
  }, []);

  useEffect(() => {
    loadOptions();
    loadBroadcasts();
  }, [loadOptions, loadBroadcasts]);

  const segment = useMemo(() => {
    if (segType === "all") return { type: "all" };
    if (segType === "members") {
      return { type: "members", member_refs: pickedMembers.map((m) => m.member_ref) };
    }
    return { type: segType, value: segValue };
  }, [segType, segValue, pickedMembers]);

  const segmentValid = useMemo(() => {
    if (segType === "all") return true;
    if (segType === "members") return pickedMembers.length > 0;
    return !!segValue;
  }, [segType, segValue, pickedMembers]);

  // 配信対象は保存しなくても、条件を変えるたびに計算し直す（誰に届くかを先に確かめる）
  const previewSeq = useRef(0);
  useEffect(() => {
    if (!segmentValid) {
      setPreview(null);
      return undefined;
    }
    const seq = ++previewSeq.current;
    const timer = setTimeout(async () => {
      setPreviewing(true);
      try {
        const data = await lineConsole.segmentPreview(segment);
        if (seq === previewSeq.current) setPreview(data);
      } catch (e) {
        if (seq === previewSeq.current) {
          setPreview(null);
          setError(`配信対象の計算に失敗しました: ${e.message}`);
        }
      } finally {
        if (seq === previewSeq.current) setPreviewing(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [segment, segmentValid]);

  const ctaError = useMemo(() => {
    if (!!ctaLabel.trim() !== !!ctaUrl.trim()) {
      return "ボタンを付けるときは、ボタン文言とリンク先の両方を入力してください";
    }
    if (ctaLabel.trim().length > CTA_LABEL_MAX) {
      return `ボタン文言は${CTA_LABEL_MAX}文字以内で入力してください`;
    }
    if (ctaUrl.trim() && !/^https?:\/\//.test(ctaUrl.trim())) {
      return "リンク先は https:// から始まるURLを入力してください";
    }
    return null;
  }, [ctaLabel, ctaUrl]);

  const fireAtIsPast = useMemo(() => {
    if (!fireAt) return false;
    return new Date(fireAt).getTime() < Date.now() - 60 * 1000;
  }, [fireAt]);

  const resetForm = () => {
    setName("");
    setBody("");
    setCtaLabel("");
    setCtaUrl("");
    setFireAt("");
    setSegType("all");
    setSegValue("");
    setPickedMembers([]);
    setCurrentId(null);
    setCurrentStatus("draft");
    setTestMember(null);
  };

  // 入力内容を保存する（新規なら作成）。保存した配信の id を返す。失敗時は null。
  const save = async () => {
    setError(null);
    if (!name.trim() || !body.trim()) {
      setError("配信名と本文は必須です。");
      return null;
    }
    if (!segmentValid) {
      setError(segType === "members" ? "送る会員を1人以上選んでください。" : "絞り込みの条件を選んでください。");
      return null;
    }
    if (ctaError) {
      setError(ctaError);
      return null;
    }
    const payload = {
      name,
      body,
      cta_label: ctaLabel.trim(),
      cta_url: ctaUrl.trim(),
      segment,
      fire_at: fireAt ? new Date(fireAt).toISOString() : null,
    };
    try {
      let bc;
      if (currentId) {
        bc = await lineConsole.updateBroadcast(currentId, payload);
      } else {
        bc = await lineConsole.createBroadcast(payload);
        setCurrentId(bc.id);
      }
      setCurrentStatus(bc.status);
      await loadBroadcasts();
      return bc.id;
    } catch (e) {
      setError(`保存に失敗しました: ${e.message}`);
      return null;
    }
  };

  const saveDraft = async () => {
    setNotice(null);
    setLoading(true);
    const id = await save();
    setLoading(false);
    if (id) {
      setNotice(
        currentStatus === "scheduled"
          ? "予約内容を更新しました（日時・配信対象の変更も予約に反映しました）。"
          : "下書きを保存しました。"
      );
    }
  };

  const confirmCount = () => (preview?.count != null ? `${preview.count} 件` : "対象");

  // 日時を指定して予約
  const doSchedule = async () => {
    setNotice(null);
    if (!fireAt) {
      setError("配信日時を指定してください（すぐ送るときは「今すぐ送信」）。");
      return;
    }
    if (fireAtIsPast) {
      setError("配信日時が過去になっています。日時を指定し直すか「今すぐ送信」を使ってください。");
      return;
    }
    const when = formatLocalInput(fireAt);
    if (!window.confirm(`${confirmCount()}に ${when} に配信します。よろしいですか？`)) return;
    setLoading(true);
    try {
      const id = await save();
      if (!id) return;
      const res = await lineConsole.schedule(id);
      setNotice(`${when} の配信を予約しました（${res.queued_count} 件）。`);
      await loadBroadcasts();
      resetForm();
    } catch (e) {
      setError(`予約に失敗しました: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  // 今すぐ送信（毎分の配信処理が次の回で送る）
  const doSendNow = async () => {
    setNotice(null);
    if (!window.confirm(`${confirmCount()}に今すぐ送信します。送信後は取り消せません。よろしいですか？`)) {
      return;
    }
    setLoading(true);
    try {
      const id = await save();
      if (!id) return;
      const res = await lineConsole.schedule(id, { sendNow: true });
      setNotice(`送信を開始しました（${res.queued_count} 件・1分以内に届きます）。`);
      await loadBroadcasts();
      resetForm();
    } catch (e) {
      setError(`送信に失敗しました: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  // テスト送信（選んだ会員だけに1通）。未保存なら先に保存する。
  const doTestSend = async () => {
    if (!testMember) return;
    setNotice(null);
    setLoading(true);
    try {
      const id = readOnly ? currentId : await save();
      if (!id) return;
      await lineConsole.testSend(id, testMember.member_ref);
      setNotice(`${testMember.member_name} さんのLINEにテスト送信しました。届いた内容を確認してください。`);
    } catch (e) {
      setError(`テスト送信に失敗しました: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const openBroadcast = async (bc) => {
    setError(null);
    setNotice(null);
    setName(bc.name || "");
    setBody(bc.body || "");
    setCtaLabel(bc.cta_label || "");
    setCtaUrl(bc.cta_url || "");
    setFireAt(bc.fire_at ? isoToLocalInput(bc.fire_at) : "");
    const seg = bc.segment || {};
    setSegType(seg.type || (bc.target_tag ? "status_tag" : "all"));
    setSegValue(seg.value || bc.target_tag || "");
    setCurrentId(bc.id);
    setCurrentStatus(bc.status);
    setTestMember(null);
    // 個別に選んだ会員は id しか保存していないので、名前を引き直す
    const refs = seg.member_refs || [];
    if (seg.type === "members" && refs.length) {
      const found = await Promise.all(
        refs.map(async (ref) => {
          try {
            const res = await lineConsole.searchMembers(String(ref));
            const hit = (res.members || []).find((m) => m.member_ref === ref);
            return hit || { member_ref: ref, member_name: `会員ID ${ref}（LINE未連携）`, display_name: "" };
          } catch (e) {
            return { member_ref: ref, member_name: `会員ID ${ref}`, display_name: "" };
          }
        })
      );
      setPickedMembers(found);
    } else {
      setPickedMembers([]);
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelBroadcast = async (bc) => {
    const verb = bc.status === "draft" ? "下書きを取り消し" : "予約を取り消し";
    if (!window.confirm(`「${bc.name}」の${verb}ますか？`)) return;
    try {
      await lineConsole.cancel(bc.id);
      setNotice(`${verb}ました。`);
      if (bc.id === currentId) resetForm();
      await loadBroadcasts();
    } catch (e) {
      setError(`取消に失敗しました: ${e.message}`);
    }
  };

  const studioNames = useMemo(
    () => Object.fromEntries((options?.studios || []).map((s) => [String(s.id), s.name])),
    [options]
  );
  const lessonNames = useMemo(
    () =>
      Object.fromEntries(
        (options?.lessons || []).map((l) => [String(l.lesson_id), `${l.studio_name} / ${l.name}`])
      ),
    [options]
  );
  const tagNames = useMemo(
    () => Object.fromEntries((options?.status_tags || []).map((t) => [t.value, t.label])),
    [options]
  );

  const segmentLabel = (seg, targetTag) => {
    const s = seg || {};
    if (!s.type) return targetTag ? `LINEステータス: ${tagNames[targetTag] || targetTag}` : "友だち全員";
    switch (s.type) {
      case "all":
        return "友だち全員";
      case "members":
        return `個別 ${s.member_refs?.length || 0} 名`;
      case "status_tag":
        return `LINEステータス: ${tagNames[s.value] || s.value}`;
      case "member_status":
        return `会員ステータス: ${MEMBER_STATUS_LABEL[s.value] || s.value}`;
      case "studio":
        return `店舗: ${studioNames[s.value] || `#${s.value}（現在は無い店舗）`}`;
      case "lesson":
        return `レッスン: ${lessonNames[s.value] || `#${s.value}（現在は無いレッスン）`}`;
      default:
        return `${s.type}:${s.value}`;
    }
  };

  const busy = loading || readOnly;

  return (
    <Box className="segment-broadcast-page" sx={{ p: 3 }}>
      <Box sx={{ maxWidth: 1100, mx: "auto" }}>
        <Typography variant="h5" fontWeight={700} gutterBottom>
          LINE セグメント配信
        </Typography>
        <Typography variant="body2" className="sb-muted" sx={{ mb: 2 }}>
          ステータス・会員状態・店舗・レッスン・個別で対象を絞ってお知らせを配信します。
          配信は1つのLINEに1通です（ご家族で1つのLINEなら1通）。配信停止・ブロックの方は自動で除外されます。
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {notice && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>
            {notice}
          </Alert>
        )}

        <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", alignItems: "flex-start" }}>
          {/* 左: 編集フォーム */}
          <Box className="sb-panel" sx={{ flex: "1 1 480px", minWidth: 360 }}>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
              {currentId ? `#${currentId}（${statusLabel(currentStatus)}）` : "新規配信"}
            </Typography>
            {readOnly && (
              <Alert severity="info" sx={{ mb: 2 }}>
                送信済みの配信は編集できません。同じ内容で送るときは「クリア」して作り直してください。
              </Alert>
            )}
            {currentStatus === "scheduled" && (
              <Alert severity="info" sx={{ mb: 2 }}>
                予約済みの配信です。変更して「予約内容を更新」すると、日時・配信対象も予約に反映されます。
              </Alert>
            )}

            <TextField
              label="配信名（管理用・お客様には表示されません）"
              fullWidth
              size="small"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={readOnly}
              sx={{ mb: 2 }}
            />

            <TextField
              label="本文"
              fullWidth
              multiline
              minRows={4}
              size="small"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              disabled={readOnly}
              helperText="{name} でLINEの表示名を差し込めます（会員名ではありません）"
              sx={{ mb: 2 }}
            />

            <Stack direction="row" spacing={2} sx={{ mb: ctaError ? 0.5 : 2 }}>
              <TextField
                label={`ボタン文言（任意・${CTA_LABEL_MAX}文字まで）`}
                size="small"
                value={ctaLabel}
                onChange={(e) => setCtaLabel(e.target.value)}
                disabled={readOnly}
                error={!!ctaError}
                sx={{ flex: 1 }}
              />
              <TextField
                label="ボタンのリンク先（任意）"
                size="small"
                value={ctaUrl}
                onChange={(e) => setCtaUrl(e.target.value)}
                disabled={readOnly}
                error={!!ctaError}
                placeholder="https://"
                sx={{ flex: 2 }}
              />
            </Stack>
            {ctaError && (
              <Typography variant="caption" color="error" display="block" sx={{ mb: 2 }}>
                {ctaError}
              </Typography>
            )}

            <TextField
              label="配信日時（予約するときだけ）"
              type="datetime-local"
              size="small"
              value={fireAt}
              onChange={(e) => setFireAt(e.target.value)}
              disabled={readOnly}
              InputLabelProps={{ shrink: true }}
              error={fireAtIsPast && !readOnly}
              helperText={
                fireAtIsPast && !readOnly
                  ? "過去の日時です。予約するときは日時を指定し直してください。"
                  : "空のままでも下書き保存・今すぐ送信ができます"
              }
              sx={{ mb: 2 }}
              fullWidth
            />

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              配信対象
            </Typography>

            <FormControl fullWidth size="small" sx={{ mb: 2 }} disabled={readOnly}>
              <InputLabel>絞り込みの軸</InputLabel>
              <Select
                label="絞り込みの軸"
                value={segType}
                MenuProps={MENU_PROPS}
                onChange={(e) => {
                  setSegType(e.target.value);
                  setSegValue("");
                }}
              >
                {SEGMENT_TYPES.map((t) => (
                  <MenuItem key={t.value} value={t.value}>
                    {t.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {segType === "status_tag" && (
              <SelectField
                label="LINEステータス"
                value={segValue}
                onChange={setSegValue}
                disabled={readOnly}
                items={(options?.status_tags || []).map((s) => ({ value: s.value, label: s.label }))}
              />
            )}
            {segType === "member_status" && (
              <SelectField
                label="会員ステータス"
                value={segValue}
                onChange={setSegValue}
                disabled={readOnly}
                items={(options?.member_statuses || []).map((s) => ({ value: s.value, label: s.label }))}
              />
            )}
            {segType === "studio" && (
              <SelectField
                label="店舗"
                value={segValue}
                onChange={setSegValue}
                disabled={readOnly}
                items={(options?.studios || []).map((s) => ({
                  value: String(s.id),
                  label: `${s.name}（${s.lesson_count}レッスン）`,
                }))}
              />
            )}
            {segType === "lesson" && (
              <SelectField
                label="レッスン"
                value={segValue}
                onChange={setSegValue}
                disabled={readOnly}
                items={(options?.lessons || []).map((l) => ({
                  value: String(l.lesson_id),
                  label: `${l.studio_name} / ${l.name}${l.day_of_week ? ` (${dayLabel(l.day_of_week)}${l.start_time ? ` ${l.start_time}` : ""})` : ""}`,
                }))}
              />
            )}
            {segType === "members" && (
              <Box sx={{ mb: 2 }}>
                <MemberSearch
                  label="会員を名前で検索して追加"
                  disabled={readOnly}
                  excludeRefs={pickedMembers.map((m) => m.member_ref)}
                  onPick={(m) => setPickedMembers((cur) => [...cur, m])}
                />
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
                  {pickedMembers.map((m) => (
                    <Chip
                      key={m.member_ref}
                      className="sb-picked"
                      label={m.member_name}
                      onDelete={
                        readOnly
                          ? undefined
                          : () => setPickedMembers((cur) => cur.filter((x) => x.member_ref !== m.member_ref))
                      }
                    />
                  ))}
                  {pickedMembers.length === 0 && (
                    <Typography variant="caption" className="sb-muted">
                      まだ誰も選ばれていません。
                    </Typography>
                  )}
                </Box>
              </Box>
            )}
            {options?.external_error && (segType === "studio" || segType === "lesson") && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                店舗/レッスンの取得に失敗しています（{options.external_error}）。
              </Alert>
            )}

            {!readOnly && (
              <Stack direction="row" spacing={2}>
                <Button variant="outlined" onClick={saveDraft} disabled={busy}>
                  {currentStatus === "scheduled" ? "予約内容を更新" : "下書きを保存"}
                </Button>
                <Button variant="text" onClick={resetForm} disabled={loading}>
                  クリア
                </Button>
              </Stack>
            )}
            {readOnly && (
              <Button variant="outlined" onClick={resetForm}>
                クリア
              </Button>
            )}
          </Box>

          {/* 右: 配信対象・テスト送信・送信 */}
          <Box className="sb-panel" sx={{ flex: "1 1 360px", minWidth: 320 }}>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
              届く人
            </Typography>
            {!segmentValid && (
              <Typography variant="body2" className="sb-muted">
                左で配信対象を選ぶと、ここに届く人が表示されます。
              </Typography>
            )}
            {segmentValid && (
              <Box sx={{ mb: 2 }}>
                <Chip
                  color="primary"
                  label={previewing ? "計算中…" : `${preview?.count ?? "-"} 件（LINEの数）`}
                  sx={{ mb: 1 }}
                />
                <Box className="sb-sample" sx={{ maxHeight: 320, overflow: "auto", p: 1, mt: 0.5 }}>
                  {(preview?.sample || []).map((u) => (
                    <Box key={u.line_user_id} className="sb-recipient">
                      <Typography variant="body2" fontWeight={600}>
                        {u.members?.length
                          ? u.members.map((m) => m.member_name).join("・")
                          : "（会員として未連携）"}
                      </Typography>
                      <Typography variant="caption" className="sb-muted">
                        LINE: {u.display_name || "(名前なし)"} ／ {u.status_label || u.status_tag}
                      </Typography>
                    </Box>
                  ))}
                  {preview && preview.count > (preview.sample || []).length && (
                    <Typography variant="caption" className="sb-muted">
                      ほか {preview.count - preview.sample.length} 件
                    </Typography>
                  )}
                  {preview && preview.count === 0 && (
                    <Typography variant="body2" className="sb-muted">
                      届く人がいません。
                    </Typography>
                  )}
                </Box>
              </Box>
            )}

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              テスト送信（選んだ1人にだけ送る）
            </Typography>
            <Typography variant="caption" className="sb-muted" display="block" sx={{ mb: 1 }}>
              自分や同僚など、LINE連携済みの会員を選んでください。送る前に内容を保存します。
            </Typography>
            {testMember ? (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                <Chip className="sb-picked" label={testMember.member_name} onDelete={() => setTestMember(null)} />
                <Button variant="outlined" onClick={doTestSend} disabled={loading}>
                  テスト送信
                </Button>
              </Stack>
            ) : (
              <Box sx={{ mb: 2 }}>
                <MemberSearch label="送り先の会員を名前で検索" disabled={loading} onPick={setTestMember} />
              </Box>
            )}

            {!readOnly && (
              <>
                <Divider sx={{ my: 2 }} />
                {/* 配信日時を入れたら予約、空なら今すぐ送信（ボタンは1つ） */}
                {fireAt ? (
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={doSchedule}
                    disabled={busy || !segmentValid || fireAtIsPast}
                  >
                    {fireAtIsPast ? "配信日時が過去です" : `${formatLocalInput(fireAt)} に配信を予約`}
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    color="error"
                    fullWidth
                    onClick={doSendNow}
                    disabled={busy || !segmentValid}
                  >
                    今すぐ送信
                  </Button>
                )}
                <Typography variant="caption" className="sb-muted" display="block" sx={{ mt: 1 }}>
                  {fireAt
                    ? "予約は一覧から取り消せます（送信後は取り消せません）。日時を空にすると今すぐ送信になります。"
                    : "1分以内に届きます。予約するときは左の「配信日時」を入力してください。"}
                </Typography>
              </>
            )}
          </Box>
        </Box>

        {/* 配信一覧 */}
        <Divider sx={{ my: 3 }} />
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
          配信一覧
        </Typography>
        <Box sx={{ overflowX: "auto" }}>
          <table className="sb-table">
            <thead>
              <tr>
                <th>#</th>
                <th>配信名</th>
                <th>状態</th>
                <th>対象</th>
                <th>送信数</th>
                <th>配信日時</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {broadcasts.map((bc) => (
                <tr key={bc.id}>
                  <td>{bc.id}</td>
                  <td>{bc.name}</td>
                  <td>{statusLabel(bc.status)}</td>
                  <td>{segmentLabel(bc.segment, bc.target_tag)}</td>
                  <td>{countLabel(bc)}</td>
                  <td>{bc.fire_at ? formatDt(bc.fire_at) : "未設定"}</td>
                  <td>
                    <Button size="small" onClick={() => openBroadcast(bc)}>
                      {bc.status === "sent" ? "見る" : "開く"}
                    </Button>
                    {(bc.status === "scheduled" || bc.status === "draft") && (
                      <Button size="small" color="error" onClick={() => cancelBroadcast(bc)}>
                        取消
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
              {broadcasts.length === 0 && (
                <tr>
                  <td colSpan={7}>まだ配信はありません。</td>
                </tr>
              )}
            </tbody>
          </table>
        </Box>
      </Box>
    </Box>
  );
}

// LINE連携済みの会員を名前で検索して1人選ぶ
function MemberSearch({ label, onPick, disabled, excludeRefs = [] }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const seq = useRef(0);

  useEffect(() => {
    const term = q.trim();
    if (!term) {
      setResults([]);
      return undefined;
    }
    const mySeq = ++seq.current;
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await lineConsole.searchMembers(term);
        if (mySeq === seq.current) setResults(res.members || []);
      } catch (e) {
        if (mySeq === seq.current) setResults([]);
      } finally {
        if (mySeq === seq.current) setSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [q]);

  const visible = results.filter((m) => !excludeRefs.includes(m.member_ref));

  return (
    <Box>
      <TextField
        label={label}
        size="small"
        fullWidth
        value={q}
        disabled={disabled}
        onChange={(e) => setQ(e.target.value)}
        placeholder="例: 山田"
        helperText="LINE連携済みの会員だけが表示されます（LINEの表示名でも探せます）"
      />
      {q.trim() && (
        <Box className="sb-search-results">
          {searching && (
            <Typography variant="caption" className="sb-muted">
              検索中…
            </Typography>
          )}
          {!searching && visible.length === 0 && (
            <Typography variant="caption" className="sb-muted">
              見つかりません（LINE連携していない会員は選べません）。
            </Typography>
          )}
          {visible.map((m) => (
            <Box
              key={m.member_ref}
              className={`sb-search-row${m.reachable ? "" : " is-unreachable"}`}
              onClick={() => {
                if (!m.reachable) return;
                onPick(m);
                setQ("");
                setResults([]);
              }}
            >
              <Typography variant="body2" fontWeight={600}>
                {m.member_name}
                <span className="sb-sample-tag">{MEMBER_STATUS_LABEL[m.member_status] || m.member_status}</span>
              </Typography>
              <Typography variant="caption" className="sb-muted">
                LINE: {m.display_name || "(名前なし)"}
                {!m.reachable && " ／ ブロック・配信停止中のため送れません"}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

function SelectField({ label, value, onChange, items, disabled }) {
  return (
    <FormControl fullWidth size="small" sx={{ mb: 2 }} disabled={disabled}>
      <InputLabel>{label}</InputLabel>
      <Select label={label} value={value} onChange={(e) => onChange(e.target.value)} MenuProps={MENU_PROPS}>
        {items.map((it) => (
          <MenuItem key={it.value} value={it.value}>
            {it.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

function statusLabel(s) {
  return { draft: "下書き", scheduled: "予約済", sent: "送信済", canceled: "取消" }[s] || s;
}

function countLabel(bc) {
  if (bc.status === "draft") return "-";
  if (bc.status === "sent") return `${bc.sent_count ?? 0} 件`;
  if (bc.status === "scheduled") {
    return bc.sent_count ? `${bc.sent_count} / ${bc.sent_count + (bc.pending_count || 0)} 件` : `${bc.pending_count ?? bc.queued_count ?? 0} 件予定`;
  }
  return bc.sent_count ? `${bc.sent_count} 件` : "-";
}

const DAY_LABEL = {
  monday: "月",
  tuesday: "火",
  wednesday: "水",
  thursday: "木",
  friday: "金",
  saturday: "土",
  sunday: "日",
};

function dayLabel(d) {
  return DAY_LABEL[String(d).toLowerCase()] || d;
}

function formatDt(iso) {
  try {
    const d = new Date(iso);
    return `${d.getFullYear()}/${d.getMonth() + 1}/${d.getDate()} ${String(d.getHours()).padStart(2, "0")}:${String(
      d.getMinutes()
    ).padStart(2, "0")}`;
  } catch (e) {
    return iso;
  }
}

// datetime-local の value → 表示用（年をまたぐ入力ミスに気づけるよう年も出す）
function formatLocalInput(v) {
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return v;
  return formatDt(d.toISOString());
}

// ISO(UTC) → datetime-local の value（ローカル時刻）
function isoToLocalInput(iso) {
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(
    d.getMinutes()
  )}`;
}

export default SegmentBroadcastPage;
